/**
 * The gradebook's columns and their properties.
 *
 * Everything that isn't a mark lives here: name, date, what it's out of, and
 * which weight category it belongs to. Adding a column lands here too, so "new
 * assessment" and "edit assessment" are the same familiar screen. No marks are
 * entered on this page.
 *
 * Laid out as the same Excel-ish table as the gradebook sheet - square corners,
 * 1px gridlines, grey header - so the two pages read as one product.
 *
 * Weight is managed per CATEGORY, not per assessment. That kills the endless
 * rebalancing: categories are set once and total 100, and adding a fourth test
 * doesn't change the fact that Tests are worth 40%.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  ArrowDown, ArrowLeft, ArrowUp, Check, ChevronDown, ChevronsUpDown,
  Plus, Scale, Split, Trash2, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import { CROSSHAIR_ROW, CROSSHAIR_TINT } from "@/gradebook/grading";
import { SLICE, effectiveWeights, toWeightRows } from "@/gradebook/categories";
import { DatePicker } from "@/gradebook/DatePicker";
import { useGradebookApi } from "@/gradebook/api";
import { useGradebookData } from "@/gradebook/useGradebookData";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NewAssessmentModal from "@/components/modals/NewAssessmentModal";
/* One source of truth for the achievement chart - see the note in
 * NewAssessmentModal. A second copy of the four category names drifts. */
import { SECTIONS, SECTION_MAX_FIELD, SECTION_NAME } from "@/gradebook/grading";

/** Borrowed from the sheet: transparent until you touch it, then a real field. */
const FIELD = [
  "h-8 border border-transparent bg-transparent px-2 text-[13px] text-foreground",
  "outline-none transition-colors hover:border-border focus:border-primary focus:bg-background",
].join(" ");

const TH = "border-b border-r border-border bg-primary px-2 py-1.5 text-left text-[13px] font-semibold uppercase tracking-wide text-primary-foreground";
const TD = "border-b border-r border-border p-0";

/** The table runs two sizes larger than the modal's fields - it is the page's
 *  whole content, not a form tucked into a dialog. `cn` (tailwind-merge) lets
 *  the override win over FIELD's own size. */
const TFIELD = "text-[15px]";

/** Ink that stays readable on each SLICE shade: the first three greens are
 *  dark enough for white, the last two light enough to need dark text.
 *  Index-aligned with SLICE - change one, change the other. */
const SLICE_INK = ["#fff", "#fff", "#fff", "hsl(160 35% 12%)", "hsl(160 35% 12%)"];

/** A sortable column header: the whole cell is the click target, and the icon
 *  answers "sorted how?" - a live arrow on the active column, a faint
 *  both-ways glyph on the idle one so the affordance is discoverable. */
function SortTh({ label, k, sort, onSort, className }) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
  return (
    <th className={cn(TH, "p-0", className)}>
      <button
        type="button"
        onClick={() => onSort(k)}
        title={`Sort by ${label.toLowerCase()}`}
        className="flex w-full items-center gap-1 px-2 py-1.5 text-left text-[13px] font-semibold uppercase tracking-wide text-primary-foreground"
      >
        {label}
        <Icon size={12} className={active ? "" : "opacity-50"} />
      </button>
    </th>
  );
}

/**
 * Ontario's achievement chart, in the order the sheet shows them.
 *
 * Kept local rather than imported from the grading core: this page needs only
 * the letters and their wire fields, and the core's own section helpers land on
 * a separate change. Four strings duplicated beats a build that depends on merge
 * order.
 */

/**
 * Which sections a row actually assesses.
 *
 * Wraps the shared `sectionsOf` only to keep ONE local nuance: `!= null` retains
 * `""`, so a teacher part-way through retyping a denominator has not momentarily
 * "deleted" the section and the row's shape does not flicker between renders
 * while they type. The shared helper sees committed data and needs no such
 * allowance; this page holds a draft.
 */
const sectionsOfDraft = (row) => SECTIONS.filter((k) => row[SECTION_MAX_FIELD[k]] != null);

/* Section maxes are Decimal(6,2) and the API rejects a `max` that isn't their
 * sum, so round at the same two places before sending: 20.1 + 15.2 must not
 * arrive as 35.300000000000004 and trip the check. */
const round2 = (n) => Math.round(n * 100) / 100;

/** Blank means "mid-edit", never zero - `0` is not a legal denominator. */
const usable = (v) => String(v ?? "").trim() !== "" && Number(v) > 0;

/* ── Manage weight categories ────────────────────────────────────────────── */

/** Push a slice out along its own mid-angle, so it lifts away from its neighbours. */
function nudge(data, index, by = 7) {
  const total = data.reduce((s, d) => s + d.weight, 0) || 1;
  let start = 0;
  for (let i = 0; i < index; i++) start += data[i].weight;
  // Recharts starts at 12 o'clock and sweeps clockwise.
  const mid = ((start + data[index].weight / 2) / total) * 2 * Math.PI;
  return `translate(${Math.sin(mid) * by}px, ${-Math.cos(mid) * by}px)`;
}

function CategoryTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, weight } = payload[0].payload;
  return (
    <div className="border border-border bg-card px-2.5 py-1.5 shadow-card">
      <p className="whitespace-nowrap text-[12px] font-medium text-foreground">{name}</p>
      <p className="text-[11px] tabular-nums text-muted-foreground">{weight}% of the mark</p>
    </div>
  );
}


/** One template for the category header, its column labels and its assessment
 *  rows, so every number in the block lines up in a single vertical rule. */
const WROW = "grid grid-cols-[14px_minmax(0,1fr)_56px_76px_28px] items-center gap-2";

function WeightsModal({ initialCats, initialRows, onSave, onClose }) {
  const [cats, setCats] = useState(() => initialCats.map((c) => ({ ...c })));
  const [rows, setRows] = useState(() => initialRows.map((r) => ({ ...r })));
  /* Which category the split editor (step 2) is open on. */
  const [activeCat, setActiveCat] = useState(() => initialCats[0]?.id ?? null);

  const total = cats.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  const balanced = total === 100;

  const patchCat = (id, changes) => setCats((l) => l.map((c) => (c.id === id ? { ...c, ...changes } : c)));
  const patchRow = (id, changes) => setRows((l) => l.map((r) => (r.id === id ? { ...r, ...changes } : r)));

  /** Give every assessment in ONE category the same share. */
  const splitCategory = (categoryId) =>
    setRows((l) => l.map((r) => (r.categoryId === categoryId ? { ...r, share: 1 } : r)));

  const effective = useMemo(() => effectiveWeights(rows, cats), [rows, cats]);

  /* Colour is taken from the category's position in the FULL list, so a slice
   * always matches its swatch even when a 0% category drops out of the donut. */
  const [hovered, setHovered] = useState(null);
  const pieData = useMemo(
    () => cats
      .map((c, i) => ({ ...c, color: SLICE[i % SLICE.length] }))
      .filter((c) => c.weight > 0),
    [cats],
  );

  /* Falls back rather than blanking: the active category can be deleted. */
  const active = cats.find((c) => c.id === activeCat) ?? cats[0] ?? null;
  const members = active ? rows.filter((r) => r.categoryId === active.id) : [];

  const STEP = "mb-2 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

  /* TWO STEPS, matching the two moments this modal is opened in. September:
     invent the categories and split 100% between them - that is step 1, with
     the donut confirming the split as it is typed, and step 2 still empty.
     Mid-year: one assessment should count double - jump to its category's tab
     in step 2 and change one number. The old layout interleaved the two jobs
     per category, so setting the year up meant scrolling past every
     assessment list to reach the next weight box. */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
      <div className="flex max-h-[88vh] w-full max-w-[760px] flex-col rounded-xl border border-border bg-card shadow-card">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <h2 className="font-display text-2xl leading-tight text-foreground">Weights</h2>
          <button
            type="button"
            onClick={onClose}
            title="Close without saving"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {/* ── Step 1: the categories and the 100% ──────────────────────── */}
          <div className="grid items-start gap-6 sm:grid-cols-[minmax(0,1fr)_200px]">
            <div>
              <span className={STEP}>1 · Categories and their share of the mark</span>
              <div className="space-y-1">
                {cats.map((c, i) => (
                  <div key={c.id} className="grid grid-cols-[14px_minmax(0,1fr)_84px_28px] items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: SLICE[i % SLICE.length] }}
                    />
                    <input
                      value={c.name}
                      onChange={(e) => patchCat(c.id, { name: e.target.value })}
                      className={cn(FIELD, "min-w-0 font-medium")}
                    />
                    <span className="flex items-center justify-end">
                      <input
                        value={c.weight}
                        inputMode="numeric"
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          patchCat(c.id, { weight: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
                        }
                        className={cn(FIELD, "w-14 px-1 text-right font-semibold")}
                      />
                      <span className="w-3 text-[13px] text-muted-foreground">%</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setCats((l) => l.filter((x) => x.id !== c.id))}
                      className="flex h-7 w-7 items-center justify-center text-muted-foreground/40 hover:text-destructive"
                      title="Remove category"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-1"
                onClick={() =>
                  setCats((l) => [...l, { id: `cat-${Date.now()}`, name: "New category", weight: 0 }])
                }
              >
                <Plus size={14} /> Category
              </Button>
            </div>

            <div>
              <div className="h-[170px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="weight"
                      nameKey="name"
                      innerRadius={38}
                      outerRadius={68}
                      stroke="hsl(var(--card))"
                      strokeWidth={2}
                      isAnimationActive={false}
                      onMouseEnter={(_, i) => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {/* The slice eases outward on hover rather than jumping:
                          the Cell keeps the same <path> across renders, so a CSS
                          transform on it actually transitions. */}
                      {pieData.map((c, i) => (
                        <Cell
                          key={c.id}
                          fill={c.color}
                          style={{
                            transform: hovered === i ? nudge(pieData, i) : "none",
                            transition: "transform 180ms ease-out",
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CategoryTip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p
                className={cn(
                  "mt-1 flex items-center justify-center gap-1.5 text-[13px] tabular-nums",
                  balanced ? "font-medium text-primary" : "text-muted-foreground",
                )}
              >
                {balanced && <Check size={14} />}
                Total {total}%
              </p>
            </div>
          </div>

          {/* ── Step 2: how one category divides ─────────────────────────── */}
          <div className="mt-6 border-t border-border pt-5">
            <span className={STEP}>2 · How each category divides between its assessments</span>
            <p className="mb-3 text-[12px] text-muted-foreground">
              Split is an assessment&apos;s share of its category - a 2 counts twice as much as a 1.
            </p>

            <div className="flex flex-wrap gap-1.5">
              {cats.map((c, i) => {
                const on = active?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActiveCat(c.id)}
                    aria-pressed={on}
                    className={cn(
                      "rounded-full px-3 py-1 text-[12.5px] font-semibold transition-opacity",
                      !on && "opacity-50 hover:opacity-90",
                    )}
                    style={{
                      background: SLICE[i % SLICE.length],
                      color: SLICE_INK[i % SLICE.length],
                      boxShadow: on
                        ? "0 0 0 2px hsl(var(--card)), 0 0 0 4px hsl(var(--primary))"
                        : undefined,
                    }}
                  >
                    {c.name} · {Number(c.weight) || 0}%
                  </button>
                );
              })}
            </div>

            {active && (
              <div className="mt-3 rounded-lg border border-border">
                {members.length === 0 ? (
                  <p className="px-3 py-3 text-[12.5px] italic text-muted-foreground/70">
                    No assessments in this category yet - new ones join it as you add them.
                  </p>
                ) : (
                  <>
                    {/* "Split" needs naming: on its own a bare 1 next to a
                        percentage looks like another weight. */}
                    <div className={cn(WROW, "px-3 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground/70")}>
                      <span />
                      <span>Assessment</span>
                      <span className="pr-1 text-right">Split</span>
                      <span className="pr-3 text-right">Weight</span>
                      <span />
                    </div>
                    <div className="divide-y divide-border">
                      {members.map((r) => (
                        <div key={r.id} className={cn(WROW, "px-3 py-1.5")}>
                          <span />
                          <span className="min-w-0 truncate text-[13px] text-foreground">
                            {r.title || "Untitled"}
                          </span>
                          <input
                            value={r.share}
                            inputMode="numeric"
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => patchRow(r.id, { share: Math.max(0, Number(e.target.value) || 0) })}
                            className={cn(FIELD, "w-full px-1 text-right")}
                          />
                          <span className="flex items-center justify-end text-[12px] font-semibold text-muted-foreground">
                            <span className="w-12 px-1 text-right tabular-nums">
                              {effective[r.id].toFixed(1)}
                            </span>
                            <span className="w-3">%</span>
                          </span>
                          <span />
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-border px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => splitCategory(active.id)}
                        className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-primary hover:underline"
                      >
                        <Split size={13} /> Split Evenly
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {/* The blocker in the app's alert red; "Ready to save." stays quiet -
              there is nothing to fix. */}
          <p className={cn("text-[12px]", balanced ? "text-muted-foreground" : "font-medium text-destructive")}>
            {balanced ? "Ready to save." : `Must total 100% - ${total > 100 ? `${total - 100} over` : `${100 - total} left`}.`}
          </p>
          <Button disabled={!balanced} onClick={() => onSave(cats, rows)}>
            Save and Exit
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
/* The gradebook now arrives from `GradebookLayout`, which owns it for the whole
   `/gradebooks/:id` section. That is what stops a hop to Insights and back from
   re-downloading every mark in the book. The layout has already dealt with
   loading and failure, so `gradebook` here is never null and this component has
   no spinner or not-found branch of its own. */
export default function GradebookAssessments() {
  const { gradebook, refetch } = useGradebookData();
  return <AssessmentsTable key={gradebook.id} gradebook={gradebook} onReload={refetch} />;
}

/**
 * Per-row debounced writer.
 *
 * Every field on this page is an uncontrolled-feeling text input, so a naive
 * save-on-change would fire a request per keystroke. Changes accumulate per row
 * id and flush once the teacher stops typing; a row edited twice before the
 * flush sends one merged PUT rather than two racing ones that could land out of
 * order.
 */
function useDebouncedRowSave(save, delay = 600) {
  const timers = useRef({});
  const pending = useRef({});

  useEffect(() => {
    const t = timers.current;
    return () => Object.values(t).forEach(clearTimeout);
  }, []);

  return useCallback(
    (id, changes) => {
      pending.current[id] = { ...(pending.current[id] ?? {}), ...changes };
      clearTimeout(timers.current[id]);
      timers.current[id] = setTimeout(() => {
        const body = pending.current[id];
        delete pending.current[id];
        delete timers.current[id];
        save(id, body).catch((err) => console.error("Failed to save assessment", err));
      }, delay);
    },
    [save, delay],
  );
}

function AssessmentsTable({ gradebook, onReload }) {
  const [params, setParams] = useSearchParams();
  const focusParam = params.get("focus");
  const api = useGradebookApi();

  const [categories, setCategories] = useState(gradebook.categories);
  const [modalOpen, setModalOpen] = useState(false);
  const [rows, setRows] = useState(
    () => toWeightRows(gradebook.assessments),
  );
  /* The highlighted row. Seeded from ?focus= (arriving from a column header on
   * the sheet) and set directly when an assessment is created on this page; the
   * row is scrolled to and flashed, then keeps a quiet marker - until the
   * teacher clicks anywhere OUTSIDE it, which lets the highlight go. A marker
   * that never released stopped meaning "this one". */
  const [focusId, setFocusId] = useState(focusParam);
  const [flash, setFlash] = useState(false);
  const focusedRow = useRef(null);
  /** Last id scrolled to, so a re-render cannot re-scroll the same row. */
  const revealed = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  /* A LATER arrival from another column header, while this page is already
   * mounted, comes in through the URL rather than through state. */
  useEffect(() => {
    if (focusParam) setFocusId(focusParam);
  }, [focusParam]);

  /* Clicking outside the highlighted row returns it to normal. The ?focus=
   * param is dropped too, so revisiting the same column header later still
   * counts as a fresh arrival. */
  useEffect(() => {
    if (!focusId) return;
    const onDown = (e) => {
      if (!focusedRow.current || focusedRow.current.contains(e.target)) return;
      setFocusId(null);
      setFlash(false);
      revealed.current = null;
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("focus");
        return next;
      }, { replace: true });
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [focusId, setParams]);

  /* The table is keyed on `gradebook.id`, so a refetch after add/delete/weights
   * re-renders WITHOUT remounting - and `rows`/`categories` are useState seeded
   * from props, whose initialisers only ever run once. Without this the server
   * round trip would land and the new row would simply never appear.
   *
   * Safe to clobber local edits here because a reload only follows an explicit
   * action (add, delete, save weights), never plain typing - and at those exact
   * moments the server copy is the one we want. */
  useEffect(() => {
    setRows(toWeightRows(gradebook.assessments));
    setCategories(gradebook.categories);
  }, [gradebook]);

  /* Depends on `rows` as well as the id: a just-created assessment is focused
   * BEFORE the reload delivers its row, so the scroll has to wait for the
   * render that actually contains it. `revealed` keeps later row updates
   * (typing in any field) from re-scrolling. */
  useEffect(() => {
    if (!focusId || revealed.current === focusId || !focusedRow.current) return;
    revealed.current = focusId;
    focusedRow.current.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlash(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 2200);
  }, [focusId, rows]);

  const saveRow = useCallback(
    (id, changes) => api.updateAssessment(gradebook.id, id, changes),
    [api, gradebook.id],
  );
  const queueSave = useDebouncedRowSave(saveRow);

  /**
   * Optimistic locally, debounced to the server.
   *
   * `title` and `max` are sanitised on the way out but NOT in local state: the
   * teacher must be able to empty a box in order to retype it, while the server
   * must never hold a nameless assessment or a null denominator. So the box goes
   * blank, and what lands in Postgres is a usable fallback.
   */
  const patch = (id, changes) => {
    setRows((list) => list.map((r) => (r.id === id ? { ...r, ...changes } : r)));

    const outgoing = { ...changes };
    if ("title" in outgoing) outgoing.title = outgoing.title.trim() || "Untitled assessment";
    if ("max" in outgoing && !(Number(outgoing.max) > 0)) delete outgoing.max;
    if (Object.keys(outgoing).length) queueSave(id, outgoing);
  };

  /**
   * Reweighting an existing split.
   *
   * The sum rule ties the four section maxes to `max`, and the API re-validates
   * the whole set on every PUT - so a section edit MUST carry the recomputed
   * total with it or the write comes back 400. They ride in one patch, which the
   * per-row debounce then merges with any sibling section edited in the same
   * breath.
   *
   * Nothing is sent while a box is empty or non-positive: that is a teacher
   * half-way through retyping `20` as `25`, not a request to drop the section.
   * Removing a section outright is deliberately NOT offered here - marks may
   * already sit under it, and discarding them is the delete-and-rebuild decision
   * the shape lock exists to make explicit.
   */
  const patchSectionMax = (row, field, raw) => {
    setRows((list) => list.map((r) => (r.id === row.id ? { ...r, [field]: raw } : r)));

    const next = { ...row, [field]: raw };
    const values = sectionsOfDraft(next).map((k) => next[SECTION_MAX_FIELD[k]]);
    if (!values.length || !values.every(usable)) return;
    queueSave(row.id, {
      [field]: Number(raw),
      max: round2(values.reduce((sum, v) => sum + Number(v), 0)),
    });
  };

  /** categoryWeight × this assessment's share of its category. */
  const effectiveWeight = useMemo(() => effectiveWeights(rows, categories), [rows, categories]);

  /* A VIEW order, never a stored one - `rows` stays in server order and the
   * sort is recomputed over it, so editing a date re-files the row where it
   * now belongs. Category sort keeps "Not counted" at the bottom in both
   * directions (it is the absence of a category, not a name starting with N)
   * and breaks ties by date so the order inside a category stays meaningful. */
  const [sort, setSort] = useState({ key: "date", dir: 1 });
  const toggleSort = (k) =>
    setSort((s) => (s.key === k ? { key: k, dir: -s.dir } : { key: k, dir: 1 }));

  const sortedRows = useMemo(() => {
    const name = (id) => categories.find((c) => c.id === id)?.name ?? "";
    const byDate = (a, b) => String(a.date).localeCompare(String(b.date));
    const list = [...rows];
    if (sort.key === "date") {
      list.sort((a, b) => sort.dir * byDate(a, b));
    } else {
      list.sort((a, b) => {
        const an = name(a.categoryId);
        const bn = name(b.categoryId);
        if (!an !== !bn) return an ? -1 : 1;
        return (sort.dir * an.localeCompare(bn)) || byDate(a, b);
      });
    }
    return list;
  }, [rows, sort, categories]);

  /* Adding goes through the sheet's own modal rather than dropping an "Untitled
   * assessment" row straight in. Single-vs-split is locked at creation - the API
   * refuses to add a split later - so a create path that can only produce singles
   * would be a dead end on the very page that manages assessment shape.
   *
   * The id comes from Postgres, and every later edit is keyed by it, so the row
   * still cannot be shown optimistically under a placeholder id: a keystroke
   * landing before the create returned would PUT to an assessment that doesn't
   * exist. */
  const [adding, setAdding] = useState(false);
  const addRow = async (body) => {
    setAdding(false);
    try {
      const created = await api.createAssessment(gradebook.id, body);
      onReload();
      /* Land the teacher on what they just made: once the reload delivers the
       * row (sorted into place by date, possibly off-screen), the scroll+flash
       * effect finds it. Setting the id also releases any other highlight. */
      revealed.current = null;
      setFocusId(created.id);
    } catch (err) {
      console.error("Failed to add assessment", err);
    }
  };

  const removeRow = async (id) => {
    setRows((list) => list.filter((x) => x.id !== id));
    try {
      await api.deleteAssessment(gradebook.id, id);
    } catch (err) {
      console.error("Failed to delete assessment", err);
      onReload(); // put the row back rather than leave a phantom deletion
    }
  };

  return (
    <PageContainer size="wide">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to={`/gradebooks/${gradebook.id}`}
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground hover:underline"
          >
            <ArrowLeft size={14} /> Back to Gradebook
          </Link>
          <h1 className="mt-2 font-display text-3xl leading-tight text-foreground">Assessments</h1>
        </div>
        {/* Default-size buttons, matching the Report Folder pages - `sm` read
            as suffocated on a page-level action. */}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setModalOpen(true)}>
            <Scale size={15} /> Manage Weights
          </Button>
          {/* Solid, and last in the row: the one action this page is for. */}
          <Button onClick={() => setAdding(true)}>
            <Plus size={15} /> Add Assessment
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              {/* Date leads, but quietly - the assessment name is what the row
                  is about. */}
              <SortTh label="Date" k="date" sort={sort} onSort={toggleSort} className="w-[128px]" />
              <th className={TH}>Assessment</th>
              {/* Right after the name it qualifies, as a quiet badge - a
                  full-width filled cell pulled the eye off the assessments. */}
              <SortTh label="Category" k="category" sort={sort} onSort={toggleSort} className="w-[170px]" />
              {/* A split's four denominators, inline in the row rather than
                  tucked under the title: they read as columns of the table, and
                  their sum feeds the Out of cell directly to their right. */}
              <th className={cn(TH, "w-[200px]")}>K/T/C/A</th>
              <th className={cn(TH, "w-[80px] text-right")}>Out of</th>
              <th className={cn(TH, "w-[88px] text-right")}>Weight</th>
              <th className={cn(TH, "w-[36px] border-r-0")} />
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((r, i) => {
              const focused = r.id === focusId;
              const secs = sectionsOfDraft(r);
              /* A split's "out of" is derived, and derived from what is on
               * screen right now - the stale server `max` would sit there
               * contradicting the boxes for the length of the debounce. */
              const liveMax = secs.every((k) => usable(r[SECTION_MAX_FIELD[k]]))
                ? round2(secs.reduce((sum, k) => sum + Number(r[SECTION_MAX_FIELD[k]]), 0))
                : r.max;
              return (
              <tr
                key={r.id}
                ref={focused ? focusedRow : null}
                className={cn(
                  "group",
                  i % 2 && "bg-[hsl(var(--neutral-500)/0.05)]",
                  focused && CROSSHAIR_TINT,
                  focused && flash && cn(CROSSHAIR_ROW, "transition-colors"),
                )}
              >
                <td className={cn(TD, focused && "border-l-2 border-l-[hsl(45_92%_50%)]")}>
                  {/* Picker-only - see DatePicker for why this is not a native
                      date input. Reads as plain text until hovered. */}
                  <DatePicker
                    value={r.date}
                    onChange={(v) => patch(r.id, { date: v })}
                    ariaLabel={`Date for ${r.title || "this assessment"}`}
                    buttonClassName="h-8 w-full border border-transparent px-2 text-left text-[14px] text-foreground transition-colors hover:border-border"
                  />
                </td>

                <td className={TD}>
                  <input
                    value={r.title}
                    placeholder="Assessment name"
                    onChange={(e) => patch(r.id, { title: e.target.value })}
                    className={cn(FIELD, TFIELD, "w-full font-medium")}
                  />
                </td>

                <td className={TD}>
                  {/* The category as a quiet pill in its SLICE shade - the same
                      by-position mapping as the weights modal's dots and donut,
                      so a colour learned there identifies the category here.
                      A custom menu, not a native <select>: the native popup
                      opened dead over the rows below and highlighted with the
                      OS's own colour, which read pinkish against our greens.
                      This one opens nudged to the RIGHT of the pill and
                      highlights in the app's amber. */}
                  {(() => {
                    const at = categories.findIndex((c) => c.id === r.categoryId);
                    const ci = at < 0 ? null : at % SLICE.length;
                    const ink = ci == null ? undefined : SLICE_INK[ci];
                    return (
                      <div className="flex h-8 items-center px-1.5">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              title={at < 0 ? "Change category" : categories[at].name}
                              className={cn(
                                /* max-w + truncate on the label: a long name
                                   clips to an ellipsis on ONE line - a pill
                                   that wraps stops reading as a pill. */
                                "inline-flex h-6 min-w-0 max-w-full cursor-pointer items-center gap-1.5 rounded-full pl-3 pr-2.5 text-[12px] font-semibold outline-none transition-shadow focus:ring-1 focus:ring-primary",
                                ci == null && "bg-muted text-muted-foreground",
                              )}
                              style={ci == null ? undefined : { background: SLICE[ci], color: ink }}
                            >
                              <span className="truncate whitespace-nowrap">
                                {at < 0 ? "Not counted" : categories[at].name}
                              </span>
                              <ChevronDown size={12} className="shrink-0" style={ink ? { color: ink } : undefined} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" alignOffset={24} sideOffset={2}>
                            <DropdownMenuItem
                              onSelect={() => patch(r.id, { categoryId: null })}
                              className="focus:bg-[hsl(45_92%_50%)]/30"
                            >
                              <span className="rounded-full bg-muted px-2.5 py-0.5 text-[12px] font-semibold text-muted-foreground">
                                Not counted
                              </span>
                            </DropdownMenuItem>
                            {categories.map((c, i) => (
                              <DropdownMenuItem
                                key={c.id}
                                onSelect={() => patch(r.id, { categoryId: c.id })}
                                className="focus:bg-[hsl(45_92%_50%)]/30"
                              >
                                <span
                                  className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold"
                                  style={{
                                    background: SLICE[i % SLICE.length],
                                    color: SLICE_INK[i % SLICE.length],
                                  }}
                                >
                                  {c.name}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })()}
                </td>

                {/* A split's shape is readable without opening anything, and
                    editable in place: the four denominators ARE the row's
                    structure, so hiding them behind a dialog would mean a
                    teacher couldn't tell two identically-named columns apart. */}
                <td className={TD}>
                  {secs.length > 0 ? (
                    <div className="flex h-8 items-center gap-x-2 pl-6 pr-1.5 text-[13px] text-muted-foreground">
                      {/* All four letters, always, so the columns line up down
                          the table. LEFT-aligned value in a two-digit box, so
                          each number reads with ITS letter rather than
                          drifting toward the next one. An unassessed section
                          shows a muted static 0 - it contributes nothing to
                          the total, and it is not editable because adding a
                          section is the shape change the API refuses. */}
                      {SECTIONS.map((k) =>
                        r[SECTION_MAX_FIELD[k]] != null ? (
                          <span key={k} className="inline-flex items-center gap-0.5">
                            <span className="font-semibold text-foreground/70">{k}</span>
                            <input
                              value={r[SECTION_MAX_FIELD[k]] ?? ""}
                              inputMode="decimal"
                              aria-label={`${SECTION_NAME[k]} out of, for ${r.title || "this assessment"}`}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => patchSectionMax(r, SECTION_MAX_FIELD[k], e.target.value)}
                              className="h-6 w-8 border border-transparent bg-transparent px-0.5 text-left text-[13px] tabular-nums text-muted-foreground outline-none transition-colors hover:border-border focus:border-primary focus:bg-background focus:text-foreground"
                            />
                          </span>
                        ) : (
                          <span key={k} className="inline-flex items-center gap-0.5 opacity-50">
                            <span className="font-semibold text-foreground/70">{k}</span>
                            <span
                              title={`${SECTION_NAME[k]} is not assessed by this task`}
                              className="w-8 px-0.5 text-left tabular-nums"
                            >
                              0
                            </span>
                          </span>
                        ),
                      )}
                    </div>
                  ) : (
                    /* An en dash, not a label: "Single mark" repeated down the
                       column outweighed the splits it was contrasting with. */
                    <span className="flex h-8 items-center pl-6 pr-2 text-[14px] text-muted-foreground/50">
                      –
                    </span>
                  )}
                </td>

                <td className={TD}>
                  {secs.length > 0 ? (
                    /* Static on a split: `max` must equal the sum of the
                       sections or the API 400s, so the one number a teacher
                       must not be able to type independently is this one. */
                    <span
                      title="Total of the K/T/C/A sections - edit those to change it"
                      className="flex h-8 items-center justify-end px-2 text-[15px] font-medium tabular-nums text-muted-foreground"
                    >
                      {liveMax ?? "-"}
                    </span>
                  ) : (
                    <input
                      value={r.max ?? ""}
                      inputMode="numeric"
                      onChange={(e) => patch(r.id, { max: Number(e.target.value) || null })}
                      className={cn(FIELD, TFIELD, "w-full text-right")}
                    />
                  )}
                </td>

                <td className={cn(TD, "px-2 text-right text-[15px] font-semibold tabular-nums text-muted-foreground")}>
                  {effectiveWeight[r.id] ? `${effectiveWeight[r.id].toFixed(1)}%` : "-"}
                </td>

                <td className={cn(TD, "border-r-0")}>
                  {/* Permanently visible, not hover-revealed: a control you can
                      only find by sweeping the mouse over rows is a control
                      teachers do not know exists. Kept very light so a column of
                      them reads as structure rather than as a row of warnings,
                      and it goes solid red under the cursor. */}
                  <button
                    type="button"
                    onClick={() => removeRow(r.id)}
                    title="Delete assessment"
                    aria-label={`Delete ${r.title || "assessment"}`}
                    className="flex h-8 w-full items-center justify-center text-muted-foreground/30 transition-colors hover:text-destructive"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {adding && (
        <NewAssessmentModal
          categories={categories}
          defaultDate={new Date().toISOString().slice(0, 10)}
          onClose={() => setAdding(false)}
          onCreate={addRow}
        />
      )}

      {modalOpen && (
        <WeightsModal
          initialCats={categories}
          initialRows={rows}
          onClose={() => setModalOpen(false)}
          onSave={async (nextCats, nextRows) => {
            setCategories(nextCats);
            setRows(nextRows);
            setModalOpen(false);
            try {
              /* Categories save as a set - the modal edits them as one, and a
               * partial write could leave the weights not totalling 100. Client
               * ids from "Add category" are stripped so the server inserts them;
               * order comes from the list's own order. */
              const saved = await api.saveCategories(
                gradebook.id,
                nextCats.map((c, i) => ({
                  ...(String(c.id).startsWith("cat-") ? {} : { id: c.id }),
                  name: c.name,
                  weight: Number(c.weight) || 0,
                  displayOrder: i,
                })),
              );

              /* A category added in this same modal session still carries its
               * client-side `cat-…` id, and an assessment may already be
               * assigned to it. Sending that id to the assessment PUT would be a
               * dangling reference, so map placeholders onto the real UUIDs the
               * save just returned - index-aligned, since both sides are ordered
               * by displayOrder. */
              const idMap = new Map(
                nextCats.map((c, i) => [c.id, saved[i]?.id ?? c.id]),
              );
              const realId = (id) => (id == null ? null : idMap.get(id) ?? id);

              /* "Split evenly" and category reassignment both land on the
               * assessments, so persist only the rows the modal actually
               * changed rather than PUTting all of them. */
              const before = new Map(rows.map((r) => [r.id, r]));
              await Promise.all(
                nextRows
                  .filter((r) => {
                    const b = before.get(r.id);
                    return b && (b.share !== r.share || b.categoryId !== r.categoryId);
                  })
                  .map((r) =>
                    api.updateAssessment(gradebook.id, r.id, {
                      share: r.share,
                      categoryId: realId(r.categoryId),
                    }),
                  ),
              );
              onReload();
            } catch (err) {
              console.error("Failed to save weights", err);
              onReload();
            }
          }}
        />
      )}
    </PageContainer>
  );
}
